/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DiagramModules
 */

// eslint-disable-next-line max-classes-per-file
import {
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import * as d3 from 'd3';
import { Store } from '@ngrx/store';
import {
    combineLatest,
    Observable,
    Subject,
    Subscription,
    takeUntil,
    Timestamp,
    timestamp as rxjsTimestamp,
} from 'rxjs';
import { copy } from 'angular';
import { debounce, throttle } from 'underscore';
import { L10nService } from '@vmw/ngx-vip';
import { num2abbrStr } from 'ng/shared/utils/math.utils';
import {
    IAviBarGraphColors,
    IAviBarGraphDataPoint,
    IAviBarGraphDataPointValue,
    IAviBarGraphDataPointValueInput,
    IAviBarGraphOverlayTimeframe,
    TTimestampFormatFromApi,
    TTimestampFormatFromApiWithMicroS,
} from 'ng/modules/diagram/components/avi-bar-graph/avi-bar-graph.types';
import { selectUtcTime } from 'ng/root-store/user-preferences/user-preferences.selectors';
import { attachComponentBindings } from 'nsx-alb-ui-components';
import {
    ILabelData,
    ILabelRowData,
    LabelCountLegendComponent,
} from 'ng/modules/diagram/components';
import {
    calculateStep,
    convertTimeToTTimestampFormatFromApi,
    getUnixTimeInMilliseconds,
} from './avi-bar-graph.utils';
import * as l10n from './avi-bar-graph.l10n';
import './avi-bar-graph.component.less';

/**
 * Height of container(s) holding bar-graph.
 */
const barGraphContainerHeightPx = 90;

/**
 * Element inside parent element must be pushed down to avoid being cut off.
 */
const pixelsFromTopOfParent = 10;

/**
 * Element inside parent element must be pushed up to avoid being cut off.
 */
const pixelsFromBottomOfParent = 20;

const maxHeightOfBars = 55;

/**
 * Height of graph container.
 */
const actualGraphHeightPx = maxHeightOfBars + pixelsFromTopOfParent + pixelsFromBottomOfParent;

/**
 * Must adjust px from left of parent to accommodate y-axis values.
 */
const pixelsFromLeftOfParent = 32;

/**
 * Desired space between individual vertical bars representing data.
 */
const pixelsBetweenBars = 1;

/**
 * Width of the overlay boundaries in graph.
 */
const overlayBorderWidth = 3;

/**
 * Number of pixels to left/right of border which can be selected for border-drag.
 */
const overlayBorderPlusMinus = overlayBorderWidth + 3;

const overlayBorderFillColorHex = '#77AECC';

/**
 * Threshold for duration between any two user interactions.
 * Event triggers in between 20 ms timespans are consolidated.
 */
const EVENTS_TRIGGER_THRESHOLD = 20;

/**
 * Constant representing elements d3 will work with.
 */
const RECT_ELEMENT = 'rect';
const G_ELEMENT = 'g';
const SVG_ELEMENT = 'svg';

const COMPONENT_NAME = 'avi-bar-graph';

/**
 * Class name constants representing classes d3 will work with.
 */
const AVI_BAR_GRAPH_VIEWPORT_CLASS = `${COMPONENT_NAME}__viewport`;
const AVI_BAR_GRAPH_SVG_CONTAINER_CLASS = `${COMPONENT_NAME}__svg-container`;
const AVI_BAR_GRAPH_SVG_PSEUDO_CONTAINER_CLASS = `${COMPONENT_NAME}__svg-pseudo-container`;
const AVI_BAR_GRAPH_SVG_OVERLAY_CONTAINER_CLASS = `${COMPONENT_NAME}__svg-overlay-container`;
const AVI_BAR_GRAPH_RECT_CLASS = `${COMPONENT_NAME}__rect`;
const AVI_BAR_GRAPH_RECT_SVG_PSEUDO_CLASS = `${COMPONENT_NAME}__rect-svg-pseudo`;
const AVI_BAR_GRAPH_RECT_ACTUAL_CLASS = `${COMPONENT_NAME}__rect-actual`;
const AVI_BAR_GRAPH_X_AXIS_CLASS = `${COMPONENT_NAME}__x-axis`;
const AVI_BAR_GRAPH_Y_AXIS_CLASS = `${COMPONENT_NAME}__y-axis`;
const AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS = `${COMPONENT_NAME}__overlay-selection`;
const AVI_BAR_GRAPH_OVERLAY_VERTICAL_CLASS = `${COMPONENT_NAME}__overlay-boundary-vertical`;
const AVI_BAR_GRAPH_OVERLAY_DRAG_START_CLASS = `${COMPONENT_NAME}__overlay-drag-start`;
const AVI_BAR_GRAPH_OVERLAY_DRAG_END_CLASS = `${COMPONENT_NAME}__overlay-drag-end`;
const AVI_BAR_GRAPH_OVERLAY_HORIZONTAL_CLASS = `${COMPONENT_NAME}__overlay-boundary-horizontal`;
const AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS = `${COMPONENT_NAME}__overlay-boundary-zoom-icon`;
const AVI_BAR_GRAPH_OVERLAY_GRADIENT_ID = `${COMPONENT_NAME}__overlay-gradient`;

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * ResizeObserver exists in browsers, but nsx-alb doesn't inherently know about it.
 * This is an 'ambient declaration', so we can declare this type.
 * Done to avoid installing an external lib (i.e. @types/resize-observer-browser) just
 * for this one use case.
 */
declare class ResizeObserver {
    constructor(callback: ResizeObserverCallback);

    public observe(target: Element): void;
    public unobserve(target: Element): void;
    public disconnect(): void;
}

type ResizeObserverCallback = (entries: any[], observer: ResizeObserver) => void;

/**
 * @description
 *      Reusable component: creates bar graph for values spanning time.
 *      Expect IAviBarGraphDataPoint[] as input to paint bars.
 * @author Akul Aggarwal
 */
@Component({
    selector: 'avi-bar-graph',
    templateUrl: './avi-bar-graph.component.html',
})
export class AviBarGraphComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * Inform if waiting for data.
     */
    @Input()
    public isLoading: boolean;

    /**
     * Inform if error present.
     */
    @Input()
    public hasError: boolean;

    /**
     * Actual data used by d3 to draw graph.
     */
    @Input()
    public dataset: IAviBarGraphDataPoint[];

    /**
     * Timestamp of duration's end => very right side of graph, where it ends.
     */
    @Input()
    public graphEndTimestamp: TTimestampFormatFromApi;

    /**
     * Timestamp of duration's start => very left side of graph, where it starts.
     */
    @Input()
    public graphStartTimestamp: TTimestampFormatFromApi;

    /**
     * Used to dictate formation of legend values. Is observable to make values variable.
     */
    @Input()
    public colorLegend$: Observable<IAviBarGraphColors>;

    /**
     * Decide if tooltip functionality will be present.
     */
    @Input()
    public allowHoverTooltip = true;

    /**
     * Text to be added to tooltip label.
     */
    @Input()
    public tooltipTotalLabelAddendum: string;

    /**
     * Handle effects on graph until api is done fetching.
     */
    @Input()
    public isGraphApiLoopInProgress: boolean;

    /**
     * Notify subscribers on graph reload, when user interacts with graph.
     */
    @Input()
    public onUserInteractionWithGraphOptionsSubject: Subject<void>;

    /**
     * Force edge bars to be of same width as rest of graph, as requested in API req.
     * Thus far specifically added in to accommodate vs-logs graph API response.
     * Needed because edge bars have varying durations, and need to be adjusted.
     * These durations can be extremely short compared to requested duration,
     * causing them to be very thin or even invisible - thus we adjust them
     * to look like the rest of the bars, while keeping the data correct.
     */
    @Input()
    public makeEdgeBarsUniformWidth = false;

    /**
     * Emitter for when user finishes creating/modifying an overlay.
     */
    @Output()
    public onOverlayCreationOrModificationOrDeletion =
            new EventEmitter<IAviBarGraphOverlayTimeframe>();

    /**
     * Emitter for when user clicks zoom button in overlay.
     */
    @Output()
    public clickOverlayZoom = new EventEmitter();

    // TODO 158220 maybe delete if can manipulate in AviBarGraphTooltip component
    /**
     * Ref to manipulate tooltip container.
     */
    @ViewChild('aviBarGraphTooltipContainerElementRef', {
        read: ElementRef,
    })
    public aviBarGraphTooltipContainerElementRef: ElementRef;

    /**
     * Ref to pass in tooltip component.
     */
    @ViewChild('aviBarGraphTooltipRef', {
        read: ViewContainerRef,
        static: true,
    })
    public aviBarGraphTooltipRef: ViewContainerRef;

    /**
     * Hold copy of dataset, to avoid modifying original data.
     */
    public datasetCopy: IAviBarGraphDataPoint[];

    /**
     * Hold copy of graph end timestamp, to avoid modifying original data.
     */
    private graphEndTimestampCopy: TTimestampFormatFromApi;

    /**
     * Hold copy of graph start timestamp, to avoid modifying original data.
     */
    private graphStartTimestampCopy: TTimestampFormatFromApi;

    public l10nKeys = l10nKeys;

    public myInjector: Injector;

    // TODO 158220 delete if works properly in AviBarGraphTooltip
    /**
     * Position of tooltip (if exists) from left of graph container.
     */
    public tooltipLeftPositionPx: number;

    /**
     * Offset of tooltip from parent's (AviBarGraph component's) left.
     */
    public tooltipOffsetX: number;

    /**
     * Width of tooltip.
     */
    public tooltipWidth: number;

    /**
     * Hold value of graph container width, before it changes.
     */
    public currentGraphContainerWidth: number;

    public labelList: ILabelData[];

    /**
     * Label displayed in summary/final line of tooltip.
     */
    public totalLogsLabel: string;

    /**
     * Value holding d3 svg graph container, including axes.
     */
    private svg: d3.Selection<SVGSVGElement, undefined, null, undefined>;

    /**
     * Value holding d3 svg graph container, minus axes.
     */
    private svgGraph: d3.Selection<SVGSVGElement, undefined, SVGSVGElement, undefined>;

    /**
     * Value holding d3 svg overlay container.
     */
    private svgOverlayContainer: d3.Selection<d3.BaseType, unknown, HTMLElement, any>;

    /**
     * Value holding d3 RECT_ELEMENT bars element selection.
     */
    private bars:
    d3.Selection<SVGSVGElement, IAviBarGraphDataPoint, SVGSVGElement, IAviBarGraphDataPoint[]>;

    /**
     * Added to component state to allow for unsubscribing.
     */
    private graphResizeObserver: ResizeObserver;

    /**
     * Boolean for start-to-end of user clicking, dragging, and releasing.
     */
    private overlayConstructionInProgress = false;

    /**
     * Boolean for start of overlay creation from user click on bar, to creation end.
     */
    private singleBarOverlayConstructionInProgress = false;

    /**
     * When drawing/manipulating graph, serves as the vertical border which has been set,
     * and is not currently moving - whether user drags left or drags right.
     * When moving entire overlay, serves as original point user dragged from.
     */
    private overlayStaticVerticalBorderPixels: DragEvent['x'];

    /**
     * Leftmost point of user click+drag.
     */
    private overlayStartPixels: DragEvent['x'];

    /**
     * Rightmost point of user click+drag.
     */
    private overlayEndPixels: DragEvent['x'];

    /**
     * Start time for overlay.
     */
    private overlayStartTime: Date;

    /**
     * End time for overlay.
     */
    private overlayEndTime: Date;

    private onUserInteractionWithGraphOptionsSubscription: Subscription;

    private onUserOverlayClickSubscription: Subscription;

    private previousOverlayEndTime: TTimestampFormatFromApiWithMicroS;

    private previousOverlayStartTime: TTimestampFormatFromApiWithMicroS;

    /**
     * Flag to check if overlay is reset upon user interaction with graph options,
     * (i.e. timeframe, log filters and refresh).
     */
    private recreateOverlay = false;

    /**
     * Flag to check if x-axis labels should follow UTC timezone.
     */
    private useUtcTime = false;

    private ngDestroyed$ = new Subject<void>();

    public tooltipHasAlerts: boolean;

    constructor(
        private readonly injector: Injector,
        // TODO 158220 delete if intermediate comp takes care of positioning
        private renderer: Renderer2,
        private readonly ngZone: NgZone,
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly l10nService: L10nService,
        private readonly store: Store,
    ) {
        l10nService.registerSourceBundles(dictionary);

        store.select(selectUtcTime).pipe(
            takeUntil(this.ngDestroyed$),
        ).subscribe(useUtcTime => this.useUtcTime = useUtcTime);
    }

    /** @override */
    public ngOnInit(): void {
        this.subscribeLegendSelectionToParamChanges();

        const totalLabel = this.l10nService.getMessage(l10nKeys.totalLabel);
        const { tooltipTotalLabelAddendum } = this;

        this.totalLogsLabel =
            tooltipTotalLabelAddendum ? `${totalLabel} ${tooltipTotalLabelAddendum}` : totalLabel;

        this.onUserInteractionWithGraphOptionsSubscription =
            this.onUserInteractionWithGraphOptionsSubject.subscribe(
                this.saveAndResetOverlayTimespan,
            );

        /**
         * User click on overlay to zoom in fires both 'onUserInteractionWithGraphOptionsSubject'
         * and 'clickOverlayZoom', in the same order. To resolve a possible race condition between
         * the two, their closest emits of them are considered [to make sure both observables are
         * fired at same time], for evaluating if overlay is clicked.
         */
        this.onUserOverlayClickSubscription = combineLatest([
            this.clickOverlayZoom.pipe(rxjsTimestamp()),
            this.onUserInteractionWithGraphOptionsSubject.asObservable().pipe(rxjsTimestamp()),
        ]).subscribe(
            eventTimestamps => this.onOverlayClick(eventTimestamps),
        );
    }

    /** @override */
    public ngOnDestroy(): void {
        if (this.graphResizeObserver) {
            this.graphResizeObserver.disconnect();
        }

        this.onUserInteractionWithGraphOptionsSubscription.unsubscribe();
        this.onUserOverlayClickSubscription.unsubscribe();

        this.ngDestroyed$.next();
        this.ngDestroyed$.complete();
    }

    /**
     * Paint d3 graph upon changes on input variables.
     * @override
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.dataset?.isFirstChange()) {
            return;
        }

        this.makeInternalCopiesOfInputs();

        if (this.makeEdgeBarsUniformWidth) {
            this.accommodateForEdgeCaseBarsAtGraphEdges();
        }

        this.refreshGraph();

        if (this.makeEdgeBarsUniformWidth) {
            this.resetEdgeCaseBarsAtGraphEdgesTimestamps();
        }

        if (!this.graphResizeObserver) {
            this.setGraphResizeObserver();
        }

        this.updateTooltipForIncomingData();
    }

    /**
     * Make copies of inputs which may undergo changes within this component.
     */
    private makeInternalCopiesOfInputs = (): void => {
        this.datasetCopy = copy(this.dataset);
        this.graphStartTimestampCopy = this.graphStartTimestamp;
        this.graphEndTimestampCopy = this.graphEndTimestamp;
    };

    /**
     * Restore edge case bars to their original timestamps, if needed.
     */
    private resetEdgeCaseBarsAtGraphEdgesTimestamps = (): void => {
        if (!this.datasetHasValues()) {
            return;
        }

        const {
            start: firstStart,
            end: firstEnd,
            realStart: firstRealStart,
        } = this.datasetCopy[0];

        const {
            end: lastEnd,
            realEnd: lastRealEnd,
            start: lastStart,
        } = this.datasetCopy[this.datasetCopy.length - 1];

        if (this.isEdgeCaseBarAtStartOfGraph(firstEnd)) {
            this.datasetCopy[0].start = firstRealStart || firstStart;
        }

        if (this.isEdgeCaseBarAtEndOfGraph(lastStart)) {
            this.datasetCopy[this.datasetCopy.length - 1].end = lastRealEnd || lastEnd;
        }
    };

    /**
     * Modify bars at very edge of left/right of graph if their duration is smaller than
     * the step (duration requested per bar in api req).
     * So far added to support API response for vs logs.
     *
     * See comment for makeEdgeBarsUniformWidth for more detail.
     */
    private accommodateForEdgeCaseBarsAtGraphEdges = (): void => {
        if (!this.datasetHasValues()) {
            return;
        }

        const { datasetCopy } = this;
        const datasetLength = datasetCopy.length;
        const possibleDatasetValuesNeedingModification = [datasetCopy[0]];

        /**
         * If no values, function returns above.
         * If 1 value, only need to check that one.
         * If more than 1 value, need to check first and last both.
         * Whether 1 value, or 2, or more, we don't know where any of them are.
         * Could be 1 or 2 at an edge, or none, or sole one at edge, or sole one in middle.
         * All we know is that only 1st and last have possibility of being at edge.
         */
        if (datasetLength > 1) {
            possibleDatasetValuesNeedingModification.push(datasetCopy[datasetLength - 1]);
        }

        const step = this.retrieveStepUsedInApi();

        [datasetCopy[0], datasetCopy[datasetLength - 1]]
            .forEach(dataPoint => {
                const { start, end } = dataPoint;

                // check if bar is at the left edge of the graph
                if (this.isEdgeCaseBarAtStartOfGraph(end)) {
                    dataPoint.realStart = start;

                    const endUnixTime = getUnixTimeInMilliseconds(end);
                    const adjustedStart = convertTimeToTTimestampFormatFromApi(endUnixTime - step * 1000);

                    this.graphStartTimestampCopy = adjustedStart;
                    dataPoint.start = adjustedStart;
                }

                // check if bar is at the right edge of the graph
                if (this.isEdgeCaseBarAtEndOfGraph(start)) {
                    dataPoint.realEnd = end;

                    const startUnixTime = getUnixTimeInMilliseconds(start);
                    const adjustedEnd = convertTimeToTTimestampFormatFromApi(startUnixTime + step * 1000);

                    this.graphEndTimestampCopy = adjustedEnd;
                    dataPoint.end = adjustedEnd;
                }
            });
    };

    private datasetHasValues = (): boolean => {
        return this.datasetCopy.length > 0;
    };

    /**
     * Inform if provided time is under step range from graph's left edge.
     */
    private isEdgeCaseBarAtStartOfGraph = (end: TTimestampFormatFromApi): boolean => {
        const barEndUnixTimeInSeconds = Math.floor(getUnixTimeInMilliseconds(end) / 1000);
        const graphStartUnixTimeInSeconds = Math.floor(getUnixTimeInMilliseconds(this.graphStartTimestampCopy) / 1000);

        const step = this.retrieveStepUsedInApi();
        const barStartToGraphStartDelta = Math.abs(barEndUnixTimeInSeconds - graphStartUnixTimeInSeconds);

        return barStartToGraphStartDelta < step;
    };

    /**
     * Inform if provided time is under step range from graph's right edge.
     */
    private isEdgeCaseBarAtEndOfGraph = (start: TTimestampFormatFromApi): boolean => {
        const barStartUnixTimeInSeconds = Math.floor(getUnixTimeInMilliseconds(start) / 1000);
        const graphEndUnixTimeInSeconds = Math.floor(getUnixTimeInMilliseconds(this.graphEndTimestampCopy) / 1000);

        const step = this.retrieveStepUsedInApi();
        const barStartToGraphStartDelta = Math.abs(barStartUnixTimeInSeconds - graphEndUnixTimeInSeconds);

        return barStartToGraphStartDelta < step;
    };

    /**
     * Retrieve step used in api request. 'step' refers to a bar's time duration in seconds.
     * Added to facilitate edge case bars with varying durations,
     * at the start and end of the graph.
     */
    private retrieveStepUsedInApi = (): number => {
        return Number(calculateStep(this.getGraphDurationInSeconds()));
    };

    /**
     * Duration between both observables, if less than threshold are consolidated as the result
     * of single user overlay click, then 'overlayClicked' is evaluated based on it.
     * Don't recreate overlay if overlay is clicked.
     */
    private onOverlayClick([overlayClick, onUserInteraction]: Array<Timestamp<unknown>>): void {
        const eventsTriggerDiff = Math.abs(overlayClick.timestamp - onUserInteraction.timestamp);
        const overlayClicked = eventsTriggerDiff < EVENTS_TRIGGER_THRESHOLD;

        if (overlayClicked) {
            this.recreateOverlay = false;
        }
    }

    /**
     * Save overlay time span before calling 'resetOverlay' method.
     */
    private readonly saveAndResetOverlayTimespan = (): void => {
        const { overlayEndTime, overlayStartTime } = this;

        this.previousOverlayEndTime = overlayEndTime;
        this.previousOverlayStartTime = overlayStartTime;
        this.resetOverlay();
        this.recreateOverlay = true;
    };

    /**
     * Create overlay from provided end time and start time.
     */
    private createOverlayIfTimeRangeValid(
        overlayEndTime: TTimestampFormatFromApiWithMicroS,
        overlayStartTime: TTimestampFormatFromApiWithMicroS,
    ): boolean {
        const isOverlayRangeWithinGraphRange =
            this.isTimeStampRangeWithinGraphRange(overlayEndTime, overlayStartTime);

        if (!isOverlayRangeWithinGraphRange) {
            return false;
        }

        const overlayStartPixels = this.getXPositionFromTimestamp(overlayStartTime);
        const overlayEndPixels = this.getXPositionFromTimestamp(overlayEndTime);

        const newGraphWidth = this.getGraphContainerWidth() - pixelsFromLeftOfParent;
        const { currentGraphContainerWidth: oldGraphWidth } = this;
        const graphWidthFraction = newGraphWidth / oldGraphWidth;

        const newXLeft = graphWidthFraction * overlayStartPixels + pixelsFromLeftOfParent;
        const newXRight = graphWidthFraction * overlayEndPixels + pixelsFromLeftOfParent;

        this.handleOverlayCreationStart(newXLeft);
        this.handleOverlayCreationInProcess(newXRight);
        this.handleOverlayCreationEnd(newXRight);
        this.updateTooltipLeftPositionPx();

        return true;
    }

    /**
     * Subscribe to boolean params for types of logs changing.
     */
    private subscribeLegendSelectionToParamChanges = (): void => {
        this.colorLegend$.subscribe(colorLegend => {
            this.labelList = Object.keys(colorLegend).map(key => {
                const {
                    color,
                    label,
                } = colorLegend[key];

                return {
                    color,
                    label,
                };
            });
        });
    };

    /**
     * Set listener:subscriber to listen to and respond to parent element size change.
     * This is run only once.
     * It must wait till d3 selection of svg is made before running.
     * ngZone needed because anything done in ResizeObserver is not in Angular's zone.
     */
    private setGraphResizeObserver = (): void => {
        this.graphResizeObserver =
            new ResizeObserver(() => {
                this.ngZone.run(this.handleGraphResize);

                if (this.getGraphContainerWidth() > 0 && this.recreateOverlay) {
                    this.ngZone.run(this.handleUserInteractionWithGraphOptions);
                    this.recreateOverlay = false;
                }
            });

        this.graphResizeObserver
            .observe(document.querySelector(`.${AVI_BAR_GRAPH_VIEWPORT_CLASS}`));
    };

    /**
     * Recreate overlay on user interaction with graph options.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly handleUserInteractionWithGraphOptions = debounce((): void => {
        const { previousOverlayEndTime, previousOverlayStartTime } = this;

        const isOverlayCreated =
            this.createOverlayIfTimeRangeValid(previousOverlayEndTime, previousOverlayStartTime);

        if (!isOverlayCreated) {
            this.onOverlayCreationOrModificationOrDeletion.emit({
                end: this.graphEndTimestampCopy,
                duration: this.getGraphDurationInSeconds(),
            });
        }
    }, 200);

    /**
     * Handle horizontal resizing of graph as needed.
     * Modifiy size and position of bars, and size and spacing of x-axis.
     * Modifiy overlay position/width, if overlay exists.
     * Throttle used to limit excessive firing, low enough number used to avoid jumpy effect.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private handleGraphResize = throttle(
        () => {
            const { currentGraphContainerWidth: oldGraphWidth } = this;
            const newGraphWidth = this.getGraphContainerWidth() - pixelsFromLeftOfParent;
            const graphWidthDelta = this.getGraphWidthDelta(newGraphWidth);

            if (graphWidthDelta === 0) {
                return;
            }

            if (this.datasetCopy.length) {
                this.setHorizontalPropsForBars();
            }

            this.svg.select<SVGSVGElement>(`.${AVI_BAR_GRAPH_X_AXIS_CLASS}`)
                // @ts-expect-error
                .call(this.scaleXAxis());

            if (this.overlayExists()) {
                const oldXLeft = this.overlayStartPixels;
                const oldXRight = this.overlayEndPixels;
                const graphWidthsFraction = newGraphWidth / oldGraphWidth;
                const newXLeft = graphWidthsFraction * oldXLeft + pixelsFromLeftOfParent;
                const newXRight = graphWidthsFraction * oldXRight + pixelsFromLeftOfParent;

                this.handleOverlayCreationStart(newXLeft, true);
                this.handleOverlayCreationInProcess(newXRight, true);
                this.handleOverlayCreationEnd(newXRight, true);
                this.updateTooltipLeftPositionPx();
            }

            this.currentGraphContainerWidth = newGraphWidth;
        },
        30,
        { leading: false },
    );

    /**
     * Return the difference between provided width and currentGraphWidth.
     */
    private getGraphWidthDelta(width: number): number {
        return this.currentGraphContainerWidth - width;
    }

    /**
     * Run after initial data retrieval, as well as any time data changes.
     * Hold seperated out methods which take care of each disparate part
     * of graph creation: axes, bars, removal, etc.
     *
     * d3 treats creation of axes, scaling, and creation of bars as separate,
     * thus these actions have been separated out in distinct methods.
     * They are ultimately bound together to the same d3 svg element selection.
     */
    private refreshGraph(): void {
        this.aviBarGraphTooltipRef.clear();
        this.singleBarOverlayConstructionInProgress = false;
        this.removeSvg();
        this.createSvg();
        this.setOverlayContainer();

        if (this.datasetCopy.length) {
            this.createGraph();
        }

        this.createYAxis();
        this.createXAxis();
        this.setOverlayColorGradient();
        this.enableOverlayCreation();
        this.enableSingleBarOverlayCreation();
        this.enableOverlayDeletionUponEmptySpaceClick();

        if (this.allowHoverTooltip) {
            this.enableTooltipCreation();
        }
    }

    /**
     * Delete 'svg' from page.
     */
    private removeSvg = (): void => {
        if (!this.overlayExists) {
            d3.select(`.${AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS}`).remove();
        }

        d3.select(`.${AVI_BAR_GRAPH_SVG_PSEUDO_CONTAINER_CLASS}`).remove();
        d3.select(`.${AVI_BAR_GRAPH_X_AXIS_CLASS}`).remove();
        d3.select(`.${AVI_BAR_GRAPH_Y_AXIS_CLASS}`).remove();
    };

    /**
     * Create 'svg' element.
     */
    private createSvg = (): void => {
        this.svg = this.svg ?? d3
            .select(`.${AVI_BAR_GRAPH_VIEWPORT_CLASS}`)
            .append(SVG_ELEMENT)
            .attr('height', barGraphContainerHeightPx)
            .attr('width', '100%')
            .classed(AVI_BAR_GRAPH_SVG_CONTAINER_CLASS, true) as typeof this.svg;

        // Separate svg container needed to represent area holding only bars, exclusive of axes.
        this.svgGraph = this.svg
            .insert(SVG_ELEMENT, ':first-child')
            .classed(AVI_BAR_GRAPH_SVG_PSEUDO_CONTAINER_CLASS, true)
            .attr('height', barGraphContainerHeightPx)
            .attr('x', pixelsFromLeftOfParent)
            .attr('width', '100%') as typeof this.svgGraph;
    };

    /**
     * Create svg overlay container.
     */
    private setOverlayContainer(): void {
        this.svgOverlayContainer = this.svgOverlayContainer ??
            d3.select(`.${AVI_BAR_GRAPH_SVG_PSEUDO_CONTAINER_CLASS}`)
                .clone()
                .classed(AVI_BAR_GRAPH_SVG_PSEUDO_CONTAINER_CLASS, false)
                .classed(AVI_BAR_GRAPH_SVG_OVERLAY_CONTAINER_CLASS, true);
    }

    /**
     * Allow drag and select of custom time range.
     */
    private enableOverlayCreation = (): void => {
        const dragOverlayInstruction = d3.drag()
            .on('start', (dragEvent: DragEvent) => this.handleOverlayCreationStart(dragEvent.x))
            // todo debounce/throttle
            .on('drag', (dragEvent: DragEvent) => this.handleOverlayCreationInProcess(dragEvent.x))
            .on('end', (dragEvent: DragEvent) => this.handleOverlayCreationEnd(dragEvent.x));

        d3.select(`.${AVI_BAR_GRAPH_SVG_CONTAINER_CLASS}`).call(dragOverlayInstruction);
    };

    /**
     * Trigger upon user clicking anywhere in graph area.
     */
    private handleOverlayCreationStart = (x: DragEvent['x'], graphResized = false): void => {
        const shiftedX = x - pixelsFromLeftOfParent;

        this.overlayStaticVerticalBorderPixels = shiftedX;

        if (!graphResized && this.hasExistingOverlayBeenClicked()) {
            return;
        }

        if (!graphResized && this.hasLeftBoundaryBeenClicked()) {
            this.overlayStaticVerticalBorderPixels = this.overlayEndPixels;
        } else if (!graphResized && this.hasRightBoundaryBeenClicked()) {
            this.overlayStaticVerticalBorderPixels = this.overlayStartPixels;
        }

        this.overlayConstructionInProgress = true;
    };

    /**
     * Trigger upon user dragging after click in graph area.
     */
    private handleOverlayCreationInProcess = (x: MouseEvent['x'], graphResized = false): void => {
        const shiftedX = x - pixelsFromLeftOfParent;

        if (!graphResized && this.hasExistingOverlayBeenClicked()) {
            this.handleOverlayDrag(shiftedX);

            return;
        }

        if (this.isOverlayBoundaryOutOfBounds(shiftedX)) {
            return;
        }

        this.resetOverlay();

        /** Draw initial vertical overlay boundary. */
        if (d3.select(`.${AVI_BAR_GRAPH_OVERLAY_DRAG_START_CLASS}`).empty()) {
            this.drawVerticalOverlayBoundary(
                this.overlayStaticVerticalBorderPixels,
                AVI_BAR_GRAPH_OVERLAY_DRAG_START_CLASS,
            );
        }

        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_DRAG_END_CLASS}`).remove();
        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS}`).remove();
        d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_HORIZONTAL_CLASS}`).remove();

        this.drawVerticalOverlayBoundary(shiftedX, AVI_BAR_GRAPH_OVERLAY_DRAG_END_CLASS);
        this.drawOverlay(shiftedX);
        this.drawHorizontalOverlayBoundaries(shiftedX);

        if (!graphResized) {
            this.createInProcessOverlayTooltip(shiftedX);
        }
    };

    /**
     * Trigger upon user letting go of mouse-click upon drag in graph area.
     */
    private handleOverlayCreationEnd = (x: DragEvent['x'], graphResized = false): void => {
        let shiftedX = x - pixelsFromLeftOfParent;

        /**
         * Terminate if user clicked, but didn't move. Because:
         * D3's 'drag' functionality has 3 parts: 'start', 'drag', and 'end'
         * 'start' and 'end' still fire if user clicked, but let go without dragging.
         */
        if (shiftedX === this.overlayStaticVerticalBorderPixels) {
            this.overlayStaticVerticalBorderPixels = undefined;
            this.overlayConstructionInProgress = false;
            d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS}`).remove();

            return;
        }

        const containerWidth = this.getGraphContainerWidth();

        /**
         * Though overlay cannot go beyond left/right of graph, the cursor can end beyond them.
         * This resets the end point(s), disallowing either one of overlay boundary left/right
         * from being set to beyond limits of graph.
         */
        if (x > containerWidth - 2) {
            shiftedX = containerWidth - pixelsFromLeftOfParent - 3;
        } else if (shiftedX < 0) {
            shiftedX = 0;
        }

        /**
         * Functionality for when overlay is being dragged.
         * Conditional in place if user drags overlay to beyond edges of graph: prevents
         * storage of overlay numbers being stored beyond edges.
         */
        if (!graphResized && this.hasExistingOverlayBeenClicked()) {
            const xDifferencePixels = shiftedX - this.overlayStaticVerticalBorderPixels;

            let overlayEndPixelsPlusDiff = this.overlayEndPixels + xDifferencePixels;
            const didCursorPositionEndBeyondRightGraphBoundary =
                overlayEndPixelsPlusDiff > containerWidth - pixelsFromLeftOfParent - 2;

            let overlayStartPixelsPlusDiff = this.overlayStartPixels + xDifferencePixels;
            const didCursorPositionEndBeyondLeftGraphBoundary = overlayStartPixelsPlusDiff < 0;

            if (didCursorPositionEndBeyondLeftGraphBoundary) {
                overlayEndPixelsPlusDiff = this.getOverlayWidthPx();
            }

            const rightEdgeOfGraph = containerWidth - pixelsFromLeftOfParent - 3;
            const leftEdgeOfGraph = 0;

            if (didCursorPositionEndBeyondRightGraphBoundary) {
                overlayStartPixelsPlusDiff = rightEdgeOfGraph - this.getOverlayWidthPx();
            }

            this.setOverlayBoundaryNumbers(
                didCursorPositionEndBeyondLeftGraphBoundary ?
                    leftEdgeOfGraph : this.adjustForOverlayEdgeOnBar(overlayStartPixelsPlusDiff),
                didCursorPositionEndBeyondRightGraphBoundary ?
                    rightEdgeOfGraph : this.adjustForOverlayEdgeOnBar(overlayEndPixelsPlusDiff, false),
            );

            this.handlePostOverlayCreation(graphResized);

            return;
        }

        const draggedLeftToRight = this.isDragFromLeftToRight(shiftedX);

        const { overlayStaticVerticalBorderPixels } = this;

        const adjustedLeft = this.adjustForOverlayEdgeOnBar(
            draggedLeftToRight ? overlayStaticVerticalBorderPixels : shiftedX,
        );

        const adjustedRight = this.adjustForOverlayEdgeOnBar(
            draggedLeftToRight ? shiftedX : overlayStaticVerticalBorderPixels,
            false,
        );

        this.setOverlayBoundaryNumbers(adjustedLeft, adjustedRight);

        this.handlePostOverlayCreation(graphResized);
    };

    /**
     * Handle remaining things once overlay is created.
     */
    private handlePostOverlayCreation = (graphResized = false): void => {
        this.overlayStaticVerticalBorderPixels = undefined;
        this.overlayConstructionInProgress = false;

        this.createZoomIconForOverlay();

        if (!graphResized) {
            this.emitOverlaySettings();
        }

        this.changeOverlayHoverCursor();
        this.handleOverlayClick();
        this.handleOverlayHover();
    };

    /**
     * Reset overlay left or right edge in case it falls within a bar's body.
     * Count full bar instead of counting partial bar time or excluding bar.
     */
    private adjustForOverlayEdgeOnBar = (overlayEdgePx: number, isLeftOverlayEdge = true): number => {
        let result = overlayEdgePx;
        const overlayEdgeTime = this.getTimeFromPixels(overlayEdgePx);
        const overlayEdgeMs = getUnixTimeInMilliseconds(overlayEdgeTime);

        this.datasetCopy.forEach(dataPoint => {
            const {
                start: dataPtStart,
                end: dataPtEnd,
            } = dataPoint;

            const dataPtEndMs = getUnixTimeInMilliseconds(dataPtEnd);
            const dataPtStartMs = getUnixTimeInMilliseconds(dataPtStart);

            if (dataPtStartMs < overlayEdgeMs && overlayEdgeMs < dataPtEndMs) {
                result = this.getXPositionFromTimestamp(isLeftOverlayEdge ? dataPtStart : dataPtEnd);
            }
        });

        return result;
    };

    /**
     * Handles user click of overlay.
     */
    private handleOverlayClick = (): void => {
        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS}`)
            .on('click', (): void => {
                this.clickOverlayZoom.emit();
            });

        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS}`)
            .on('click', (): void => {
                this.clickOverlayZoom.emit();
                d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS}`)
                    .remove();
            });
    };

    /**
     * Handle mouseover event on completed overlay.
     */
    private handleOverlayHover = (): void => {
        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS}`)
            .on('mousemove', (d: MouseEvent) => {
                this.createCompletedOverlayTooltip(d.offsetX);
            })
            .on('mouseout', () => {
                this.aviBarGraphTooltipRef.clear();
            });
    };

    /**
     * Create tooltip over completed overlay.
     */
    private createCompletedOverlayTooltip = (x: MouseEvent['x']): void => {
        const { overlayStartPixels: start, overlayEndPixels: end } = this;

        this.createTooltip(
            this.getAviBarGraphDataPointFromTimeframe(start, end),
            x + this.getNavigationWidth(),
        );
    };

    /**
     * Emit to parent when overlay is created/altered.
     */
    private emitOverlaySettings = (): void => {
        const duration = this.getOverlayDuration() / 1000;

        this.onOverlayCreationOrModificationOrDeletion.emit({
            end: this.overlayEndTime,
            duration,
        });
    };

    /**
     * Create zoom-in icon upon overlay creation/modification.
     */
    private createZoomIconForOverlay = (): void => {
        d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS}`).remove();

        if (this.getOverlayDuration() / 1000 < 120) {
            return;
        }

        const zoomIcon = document.createElement('cds-icon');

        zoomIcon.shape = 'zoom-in';

        const pxFromBottom = maxHeightOfBars + 11;
        const pxFromLeft = this.getOverlayMiddlePixels();

        d3.select(`.${AVI_BAR_GRAPH_VIEWPORT_CLASS}`)
            .insert(() => zoomIcon)
            .classed(AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS, true)
            .style('position', 'relative')
            .style('bottom', `${pxFromBottom}px`)
            .style('left', `${pxFromLeft}px`);
    };

    /**
     * Get horizontal pixels from left of graph of overlay middle.
     */
    private getOverlayMiddlePixels = (): number =>
        pixelsFromLeftOfParent - 6 + this.overlayStartPixels +
            (this.overlayEndPixels - this.overlayStartPixels) / 2;

    /**
     * Set pixels from left of graph and time for overlay start and end.
     */
    private setOverlayBoundaryNumbers = (startPixels: number, endPixels: number): void => {
        this.overlayStartPixels = startPixels;
        this.overlayEndPixels = endPixels;
        this.overlayStartTime = this.getTimeFromPixels(this.overlayStartPixels);
        this.overlayEndTime = this.getTimeFromPixels(this.overlayEndPixels);
    };

    /**
     * Inform when left/right boundary is beyond x-axis of graph.
     */
    private isOverlayBoundaryOutOfBounds = (boundary: DragEvent['x']): boolean => {
        const graphRightExtreme = this.getGraphContainerWidth() -
            pixelsFromLeftOfParent - overlayBorderWidth;

        return boundary < 0 || boundary > graphRightExtreme;
    };

    /**
     * Set cursor to desired upon mouse hover.
     */
    private changeOverlayHoverCursor = (): void => {
        d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_VERTICAL_CLASS}`)
            .on('mouseover', function() {
                d3.select(this)
                    .style('cursor', 'ew-resize');
            });

        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS}`)
            .on('mouseover', function() {
                d3.select(this)
                    .style('cursor', 'move');
            });

        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS}`)
            .on('mouseover', function() {
                d3.select(this)
                    .style('cursor', 'pointer');
            });
    };

    /**
     * Create overlay upon click-selection of single data-bar.
     */
    private enableSingleBarOverlayCreation = (): void => {
        d3.selectAll(`.${AVI_BAR_GRAPH_RECT_SVG_PSEUDO_CLASS}`)
            .on('click', ev => {
                this.singleBarOverlayConstructionInProgress = true;
                this.resetOverlay();

                const { __data__: data } = ev.currentTarget;
                let x1 = this.getXPositionFromTimestamp(data.start);
                let x2 = this.getXPositionFromTimestamp(data.end);

                /*
                 * Overlay border width subtracted to ensure overlay 'inside area'
                 * represent the actual data-bar.
                 */
                this.setOverlayBoundaryNumbers(x1, x2);
                x1 -= overlayBorderWidth;
                x2 -= overlayBorderWidth;

                this.overlayStaticVerticalBorderPixels = x1;
                this.drawVerticalOverlayBoundary(x1, AVI_BAR_GRAPH_OVERLAY_DRAG_START_CLASS);
                this.drawVerticalOverlayBoundary(x2, AVI_BAR_GRAPH_OVERLAY_DRAG_END_CLASS);
                this.drawOverlay(x2);
                this.drawHorizontalOverlayBoundaries(x2);

                this.handlePostOverlayCreation();
            });
    };

    /**
     * Delete overlay if user clicks space outside overlay and between bars.
     */
    private enableOverlayDeletionUponEmptySpaceClick = (): void => {
        d3.selectAll(`.${AVI_BAR_GRAPH_SVG_CONTAINER_CLASS}`)
            .on('click', () => {
                if (this.singleBarOverlayConstructionInProgress) {
                    this.singleBarOverlayConstructionInProgress = false;

                    return;
                }

                if (this.overlayExists()) {
                    d3.select(`.${AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS}`)
                        .remove();

                    d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_HORIZONTAL_CLASS}`)
                        .remove();

                    d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_VERTICAL_CLASS}`)
                        .remove();

                    this.onOverlayCreationOrModificationOrDeletion.emit({
                        end: this.graphEndTimestampCopy,
                        duration: this.getGraphDurationInSeconds(),
                    });
                }
            });
    };

    /**
     * Aggregate data point information in given timeframe.
     */
    private getAviBarGraphDataPointFromTimeframe =
        (startPx: number, endPx: number): IAviBarGraphDataPoint => {
            const start = this.getTimeFromPixels(startPx);
            const end = this.getTimeFromPixels(endPx);
            const initialValue: IAviBarGraphDataPoint = {
                end,
                start,
                totalValueSummation: 0,
                values: [],
                tooltipValues: [],
            };

            return this.datasetCopy
                .reduce((previousDataPoint, currentDataPoint) => {
                    const {
                        start: currentDataPtStart,
                        end: currentDataPtEnd,
                    } = currentDataPoint;

                    const overlayEndMs = getUnixTimeInMilliseconds(end);
                    const overlayStartMs = getUnixTimeInMilliseconds(start);
                    const currentDataPtEndMs = getUnixTimeInMilliseconds(currentDataPtEnd);
                    const currentDataPtStartMs = getUnixTimeInMilliseconds(currentDataPtStart);
                    const fallsOutsideOverlay =
                        overlayEndMs < currentDataPtEndMs || currentDataPtStartMs < overlayStartMs;

                    if (fallsOutsideOverlay) {
                        return previousDataPoint;
                    }

                    const {
                        values: previousValues,
                        tooltipValues: previousToolTipValues,
                        totalValueSummation: previousTotal,
                    } = previousDataPoint;
                    const {
                        values: currentValues,
                        tooltipValues: currentToolTipValues,
                        totalValueSummation: currentTotal,
                    } = currentDataPoint;

                    const previousValueTypesTuple =
                        [previousValues.map(({ type }) => type), previousToolTipValues.map(({ type }) => type)];
                    // TODO Delete when library "@types/node" is upgraded to v17
                    const valuesTuple = [copy(previousValues), copy(previousToolTipValues)];
                    const currentValuesTuple = [copy(currentValues), copy(currentToolTipValues)];

                    currentValuesTuple.forEach((presentValues, i) => {
                        presentValues.forEach(presentValue => {
                            const {
                                type: currentType,
                                count,
                            } = presentValue;
                            const currentTypeIndex = previousValueTypesTuple[i].indexOf(currentType);

                            if (currentTypeIndex === -1) {
                                valuesTuple[i].push(presentValue);
                            } else {
                                valuesTuple[i][currentTypeIndex].count += count;
                            }
                        });
                    });

                    return {
                        end,
                        start,
                        totalValueSummation: previousTotal + currentTotal,
                        values: valuesTuple[0],
                        tooltipValues: valuesTuple[1],
                    };
                }, initialValue);
        };

    /**
     * Create tooltip over overlay while overlay is being created.
     */
    private createInProcessOverlayTooltip = (x: number): void => {
        const { overlayStaticVerticalBorderPixels } = this;
        const draggedLeftToRight = this.isDragFromLeftToRight(x);
        const leftPx = draggedLeftToRight ? overlayStaticVerticalBorderPixels : x;
        const rightPx = draggedLeftToRight ? x : overlayStaticVerticalBorderPixels;

        this.createTooltip(
            this.getAviBarGraphDataPointFromTimeframe(leftPx, rightPx),
            x + this.getNavigationWidth(),
        );
    };

    /**
     * Handle tooltip creation.
     */
    private enableTooltipCreation = (): void => {
        this.enableTooltipCreationUponHoverBars();
        this.enableTooltipDefaultCreationForOverlay();
    };

    /**
     * Handle tooltip creation upon user hover over data bars.
     */
    private enableTooltipCreationUponHoverBars = (): void => {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const barGraphInstanceContext = this;

        d3.selectAll(`.${AVI_BAR_GRAPH_RECT_SVG_PSEUDO_CLASS}`)
            .on('mouseover', function(d: MouseEvent) {
                barGraphInstanceContext
                    .createTooltip(
                        d3.select(this).data()[0] as IAviBarGraphDataPoint,
                        d.offsetX + barGraphInstanceContext.getNavigationWidth(),
                    );

                d3.selectAll(`.${AVI_BAR_GRAPH_RECT_CLASS}`)
                    .style('opacity', 0.5);

                // eslint-disable-next-line
                const hovered = event.target as Element;
                const parent = hovered.parentNode as Element;
                const siblings =
                    Array.from(parent.querySelectorAll(`.${AVI_BAR_GRAPH_RECT_CLASS}`));

                siblings.forEach(sibling => {
                    d3.select(sibling)
                        .style('opacity', 1);
                });
            })
            .on('mouseout', function() {
                barGraphInstanceContext.aviBarGraphTooltipRef.clear();
                d3.selectAll(`.${AVI_BAR_GRAPH_RECT_CLASS}`)
                    .style('opacity', 1);
            });
    };

    /**
     * Return left navigation menu's width.
     * Tooltip uses this to determine where to position itself.
     */
    private getNavigationWidth = (): number => {
        return document.querySelector('.clr-vertical-nav').getBoundingClientRect().width - 10;
    };

    /**
     * Handle tooltip creation upon mouse exit of main graph svg container.
     */
    private enableTooltipDefaultCreationForOverlay = (): void => {
        d3.select(`.${AVI_BAR_GRAPH_SVG_CONTAINER_CLASS}`)
            .on('mouseenter', () => {
                if (!this.overlayConstructionInProgress) {
                    this.aviBarGraphTooltipRef.clear();
                }
            })
            .on('mouseleave', () => {
                if (!this.overlayExists()) {
                    return;
                }

                this.createCompletedOverlayTooltip(this.getOverlayMiddlePixels());
            });
    };

    /**
     * Create hover tooltip to display desired data info.
     */
    private createTooltip = (data: IAviBarGraphDataPoint, x: number): void => {
        const componentRef = this.createTooltipComponentRef();
        const { aviBarGraphTooltipRef } = this;

        const {
            end,
            start,
            tooltipValues,
        } = data;

        const transformedData = this.transformDataToTooltipInput(tooltipValues);
        const labelList = transformedData[0];
        const alertMessages = transformedData[1];

        /**
         * Done to remove summation/total count from data.
         */
        const summarySectionLabel = labelList.shift();

        this.tooltipHasAlerts = alertMessages.length > 0;

        const componentBindings = {
            title: this.getTooltipTitle(start, end),
            labelList,
            summarySectionLabel,
            alertMessages,
        };

        attachComponentBindings(
            componentRef,
            componentBindings,
        );

        aviBarGraphTooltipRef.insert(componentRef.hostView);

        // TODO 158220 delete
        this.tooltipLeftPositionPx = x - pixelsFromLeftOfParent * 2;

        setTimeout(() => {
            this.tooltipWidth =
                this.aviBarGraphTooltipContainerElementRef.nativeElement.offsetWidth;

            // TODO delete 158220
            // console.log(this.tooltipWidth);
        }, 0);

        // TODO 158220 Delete if keeping avi-bar-graph-tooltip.component
        // this.renderer.setStyle(
        //     aviBarGraphTooltipContainerElementRef.nativeElement,
        //     'tooltipLeftPositionPx', `${d.offsetX - pixelsFromLeftOfParent * 2}px`,
        // );
    };

    /**
     * Return tooltipref element.
     */
    private createTooltipComponentRef = (): ComponentRef<Component> => {
        const { aviBarGraphTooltipRef } = this;

        aviBarGraphTooltipRef.clear();

        const componentFactory = this.componentFactoryResolver
            .resolveComponentFactory(LabelCountLegendComponent as Type<Component>);

        return componentFactory.create(aviBarGraphTooltipRef.injector);
    };

    /**
     * Transform data to format needed by tooltip.
     * Return tuple which contains: [normal data, alert data].
     */
    private transformDataToTooltipInput =
        (values: IAviBarGraphDataPointValueInput[]): [ILabelRowData[], ILabelRowData[]] => {
            const result: [ILabelRowData[], ILabelRowData[]] = [[], []];

            values.forEach((val: IAviBarGraphDataPointValueInput) => {
                const {
                    color,
                    count,
                    type: label,
                    forceAlign,
                    alert,
                    description,
                } = val;

                if (alert) {
                    result[1].push({
                        count,
                        label,
                        description,
                    });

                    return;
                }

                result[0].push({
                    color,
                    count,
                    label,
                    forceAlign: !!forceAlign,
                });
            });

            return result;
        };

    /**
     * Transform start & end times into title to display in tooltip.
     */
    private getTooltipTitle =
        (start: TTimestampFormatFromApi, end: TTimestampFormatFromApi): string => {
            const { formatTimeForDisplay } = this;
            const isMoreThan24Hours =
                this.getDurationInMilliSeconds(end, start) > 86400000;

            const formatTime = isMoreThan24Hours ?
                formatTimeForDisplay('%m/%d %I:%M:%S %p') : formatTimeForDisplay('%I:%M:%S %p');
            const titleStart = formatTime(new Date(getUnixTimeInMilliseconds(start)));
            const titleEnd = formatTime(new Date(getUnixTimeInMilliseconds(end)));

            return `${titleStart} - ${titleEnd}`;
        };

    /**
     * Determine if overlay exists or is in process of being made.
     */
    private overlayExists = (): boolean => {
        return !d3.select(`.${AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS}`).empty();
    };

    /**
     * Determine if left boundary has been clicked.
     */
    private hasLeftBoundaryBeenClicked = (): boolean => {
        const {
            overlayStaticVerticalBorderPixels,
            overlayStartPixels,
        } = this;

        return overlayStaticVerticalBorderPixels >
            overlayStartPixels - overlayBorderPlusMinus &&
            overlayStaticVerticalBorderPixels <
            overlayStartPixels + overlayBorderPlusMinus;
    };

    /**
     * Determine if right boundary has been clicked.
     */
    private hasRightBoundaryBeenClicked = (): boolean => {
        const {
            overlayStaticVerticalBorderPixels,
            overlayEndPixels,
        } = this;

        return overlayStaticVerticalBorderPixels >
            overlayEndPixels - overlayBorderPlusMinus &&
            overlayStaticVerticalBorderPixels < overlayEndPixels + overlayBorderPlusMinus;
    };

    /**
     * Draw custom overlay.
     * Represent:
     *  when overlay is being created or a boundary is being dragged, the current cursor position.
     *  when overlay is being moved, old left boundary + number of pixels being moved.
     */
    private drawOverlay = (x: DragEvent['x'], isOverlayBeingDragged = false): void => {
        const widthPixels = isOverlayBeingDragged ?
            this.overlayEndPixels - this.overlayStartPixels :
            this.getOverlayWidthPxDuringConstruction(x);

        /**
         * Represent:
         *  when overlay created left to right, left boundary.
         *  when overlay created right to left, current cursor position.
         *  when overlay is being moved, old left boundary + number of pixels being moved.
         */
        let startingPositionPixels: number;

        if (isOverlayBeingDragged || !this.isDragFromLeftToRight(x)) {
            startingPositionPixels = x;
        } else {
            startingPositionPixels = this.overlayStaticVerticalBorderPixels;
        }

        this.svgOverlayContainer
            .append(RECT_ELEMENT)
            .classed(AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS, true)
            .attr('x', startingPositionPixels + 2)
            .attr('y', pixelsFromTopOfParent)
            .attr('width', widthPixels)
            .attr('height', maxHeightOfBars)
            .attr('fill', `url(#${AVI_BAR_GRAPH_OVERLAY_GRADIENT_ID})`);
    };

    /**
     * Draw left/right boundary for custom overlay.
     */
    private drawVerticalOverlayBoundary = (xPosition: number, borderUniqueClass: string): void => {
        this.svgOverlayContainer
            .append(RECT_ELEMENT)
            .classed(AVI_BAR_GRAPH_OVERLAY_VERTICAL_CLASS, true)
            .classed(borderUniqueClass, true)
            .attr('x', xPosition)
            .attr('y', pixelsFromTopOfParent - overlayBorderWidth)
            .attr('width', overlayBorderWidth)
            .attr('height', maxHeightOfBars + overlayBorderWidth)
            .attr('fill', overlayBorderFillColorHex);
    };

    /**
     * Determine if user click occurred inside existing overlay.
     */
    private hasExistingOverlayBeenClicked = (): boolean => {
        if (!this.overlayExists() || this.overlayConstructionInProgress) {
            return false;
        }

        const { overlayStaticVerticalBorderPixels: x } = this;

        const isRightOfBorderPlusMargin = x > this.overlayStartPixels + overlayBorderPlusMinus;
        const isLeftOfBorderPlusMargin = x < this.overlayEndPixels - overlayBorderPlusMinus;

        if (isRightOfBorderPlusMargin && isLeftOfBorderPlusMargin) {
            return true;
        }

        return false;
    };

    /**
     * Reset all overlay properties and deletes existing overlay.
     */
    private resetOverlay = (): void => {
        this.overlayStartPixels = undefined;
        this.overlayEndPixels = undefined;
        this.overlayStartTime = undefined;
        this.overlayEndTime = undefined;
        this.removeOverlayGraphics();
    };

    /**
     * Remove existing overlay from page. Does not delete properties.
     */
    private removeOverlayGraphics = (): void => {
        d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_DRAG_START_CLASS}`).remove();
        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_SELECTION_CLASS}`).remove();
        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_DRAG_END_CLASS}`).remove();
        d3.selectAll(`.${AVI_BAR_GRAPH_OVERLAY_HORIZONTAL_CLASS}`).remove();
        d3.select(`.${AVI_BAR_GRAPH_OVERLAY_ZOOM_ICON_CLASS}`).remove();
    };

    /**
     * Drag overlay.
     */
    private handleOverlayDrag = (currentX: DragEvent['x']): void => {
        const xDifferencePixels = currentX - this.overlayStaticVerticalBorderPixels;

        const newLeftBoundaryPixels = this.overlayStartPixels + xDifferencePixels;
        const newRightBoundaryPixels = this.overlayEndPixels + xDifferencePixels;

        if (this.isOverlayBoundaryOutOfBounds(newLeftBoundaryPixels) ||
            this.isOverlayBoundaryOutOfBounds(newRightBoundaryPixels)) {
            return;
        }

        this.removeOverlayGraphics();

        this.drawVerticalOverlayBoundary(
            newLeftBoundaryPixels,
            AVI_BAR_GRAPH_OVERLAY_DRAG_START_CLASS,
        );
        this.drawVerticalOverlayBoundary(
            newRightBoundaryPixels,
            AVI_BAR_GRAPH_OVERLAY_DRAG_END_CLASS,
        );
        this.drawOverlay(newLeftBoundaryPixels, true);
        this.drawHorizontalOverlayBoundaries(newLeftBoundaryPixels, true);
    };

    /**
     * Set gradient for overlay.
     */
    private setOverlayColorGradient = (): void => {
        const gradientElement = this.svgGraph
            .append('linearGradient')
            .attr('id', AVI_BAR_GRAPH_OVERLAY_GRADIENT_ID)
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', 0)
            .attr('y2', 1);

        gradientElement
            .append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#67b0cb') // aviBlue3
            .attr('stop-opacity', 0.3)
            .attr('offset', 0);

        gradientElement
            .append('stop')
            .attr('offset', '100%')
            .attr('stop-color', 'white')
            .attr('stop-opacity', 0)
            .attr('offset', 0.6);
    };

    /**
     * Draw top and bottom boundaries for custom overlay.
     */
    private drawHorizontalOverlayBoundaries =
        (x: DragEvent['x'], isOverlayBeingDragged = false): void => {
            const widthPixels = isOverlayBeingDragged ?
                this.overlayEndPixels - this.overlayStartPixels :
                this.getOverlayWidthPxDuringConstruction(x);

            let startingPositionPixels: number;

            if (isOverlayBeingDragged || !this.isDragFromLeftToRight(x)) {
                startingPositionPixels = x;
            } else {
                startingPositionPixels = this.overlayStaticVerticalBorderPixels;
            }

            // Top bar
            this.svgOverlayContainer
                .append(RECT_ELEMENT)
                .classed(AVI_BAR_GRAPH_OVERLAY_HORIZONTAL_CLASS, true)
                .attr('x', startingPositionPixels)
                .attr('y', pixelsFromTopOfParent - overlayBorderWidth)
                .attr('width', widthPixels)
                .attr('height', overlayBorderWidth)
                .attr('fill', overlayBorderFillColorHex);

            // Bottom bar
            this.svgOverlayContainer
                .append(RECT_ELEMENT)
                .classed(AVI_BAR_GRAPH_OVERLAY_HORIZONTAL_CLASS, true)
                .attr('x', startingPositionPixels)
                .attr('y', maxHeightOfBars + pixelsFromTopOfParent - overlayBorderWidth)
                .attr('width', widthPixels)
                .attr('height', overlayBorderWidth)
                .attr('fill', overlayBorderFillColorHex);
        };

    /**
     * Return width of overlay during construction of overlay.
     */
    private getOverlayWidthPxDuringConstruction = (currentX: DragEvent['x']): number => {
        return Math.abs(currentX - this.overlayStaticVerticalBorderPixels);
    };

    /**
     * Return width of overlay after it is created.
     */
    private getOverlayWidthPx = (): number => {
        if (!this.overlayStartPixels) {
            return 0;
        }

        return this.overlayEndPixels - this.overlayStartPixels;
    };

    /**
     * Return duration of overlay in milliseconds.
     */
    private getOverlayDuration = (): number => {
        if (!this.overlayStartTime) {
            return 0;
        }

        return getUnixTimeInMilliseconds(this.overlayEndTime) -
            getUnixTimeInMilliseconds(this.overlayStartTime);
    };

    /**
     * Determine if user went left->right or right->left.
     */
    private isDragFromLeftToRight = (currentX: DragEvent['x']): boolean => {
        return currentX > this.overlayStaticVerticalBorderPixels;
    };

    /**
     * Convert pixels from left extreme of graph to time, in a readable format.
     */
    private getTimeFromPixels =
        (pixelsFromLeft: number): Date => {
            const {
                graphEndTimestampCopy,
                graphStartTimestampCopy,
            } = this;

            const ratioFromLeft =
                pixelsFromLeft / (this.getGraphContainerWidth() - pixelsFromLeftOfParent);
            const milliSecondsFromStart = ratioFromLeft *
                this.getDurationInMilliSeconds(graphEndTimestampCopy, graphStartTimestampCopy);
            const unixTime = milliSecondsFromStart +
                getUnixTimeInMilliseconds(graphStartTimestampCopy);

            return convertTimeToTTimestampFormatFromApi(unixTime);
        };

    /**
     * Return true, if provided timestamp is within the graph range, false if otherwise.
     */
    private isTimeStampInGraphRange(timeStamp: TTimestampFormatFromApi): boolean {
        const isGreaterThanGraphStartTimeStamp =
            this.getDurationInMilliSeconds(timeStamp, this.graphStartTimestampCopy) >= 0;
        const isLessThanGraphEndTimeStamp =
            this.getDurationInMilliSeconds(this.graphEndTimestampCopy, timeStamp) >= 0;

        return isGreaterThanGraphStartTimeStamp && isLessThanGraphEndTimeStamp;
    }

    /**
     * Return true, if provided start and end timestamps fall within the range of graph,
     * false if otherwise.
     */
    private isTimeStampRangeWithinGraphRange(
        endTimeStamp: TTimestampFormatFromApi,
        startTimeStamp: TTimestampFormatFromApi,
    ): boolean {
        return this.isTimeStampInGraphRange(startTimeStamp) &&
            this.isTimeStampInGraphRange(endTimeStamp);
    }

    /**
     * Main functionality creating actual bars in graph from data.
     */
    private createGraph = (): void => {
        this.bars = this.svgGraph.selectAll(`.${AVI_BAR_GRAPH_RECT_CLASS}`)
            .data(this.datasetCopy)
            .join(SVG_ELEMENT) // extra full size pseudo-element
            .classed(AVI_BAR_GRAPH_RECT_SVG_PSEUDO_CLASS, true) as typeof this.bars;

        this.addBars();
        this.setHorizontalPropsForBars();
        this.changeGraphCursor();
    };

    /**
     * Set cursor to desired upon mouse hover over graph area.
     */
    private changeGraphCursor = (): void => {
        d3.select(`.${AVI_BAR_GRAPH_SVG_CONTAINER_CLASS}`)
            .on('mouseover', function(): void {
                d3.select(this).style('cursor', 'crosshair');
            });
    };

    /**
     * Add actual bars from data.
     */
    private addBars = (): void => {
        this.bars.selectAll(`.${AVI_BAR_GRAPH_RECT_CLASS}`)
            .data((datum: IAviBarGraphDataPoint): IAviBarGraphDataPointValue[] => {
                const {
                    end,
                    start,
                    totalValueSummation,
                } = datum;

                const { totalLogsLabel } = this;

                let lastCount = 0;

                // Pseudo-bar to represent full column and
                // total sum data for tooltip
                const tooltipValuesSummation: IAviBarGraphDataPointValueInput = {
                    count: totalValueSummation,
                    type: totalLogsLabel,
                };
                const valuesSummation = copy(tooltipValuesSummation);

                tooltipValuesSummation.forceAlign = false;

                // For tooltip summation row
                datum.tooltipValues.unshift(tooltipValuesSummation);

                valuesSummation.color = '#fafafa';
                // yPosition for this will be incorrect, and handled separately
                datum.values.unshift(valuesSummation);

                // Return actual properties needed to create bars
                return datum.values
                    .map((value: IAviBarGraphDataPointValueInput): IAviBarGraphDataPointValue => {
                        const {
                            color,
                            type,
                        } = value;

                        let { count } = value;

                        if (type === totalLogsLabel) {
                            count = 0;
                        }

                        const currentCount = lastCount;

                        lastCount += count;

                        return {
                            color,
                            count,
                            end,
                            start,
                            type,
                            yPosition: currentCount + count,
                        };
                    });
            })
            .join(RECT_ELEMENT)
            .classed(AVI_BAR_GRAPH_RECT_CLASS, true)
            .classed(AVI_BAR_GRAPH_RECT_ACTUAL_CLASS, true)
            // Represent top of the bar in pixels
            .attr('y', (value: IAviBarGraphDataPointValue): number => {
                // set to match max y-value out of all columns for pseudo-bar
                if (value.type === this.totalLogsLabel) {
                    return pixelsFromTopOfParent;
                }

                return actualGraphHeightPx - pixelsFromBottomOfParent -
                this.createBarYScale()(value.yPosition);
            })
            .attr('height', (value: IAviBarGraphDataPointValue): number => {
                // set to match max count out of all data for pseudo-bar
                if (value.type === this.totalLogsLabel) {
                    return maxHeightOfBars;
                }

                return this.createBarYScale()(value.count);
            })
            .attr('fill', (value: IAviBarGraphDataPointValue):
            IAviBarGraphDataPointValue['color'] => value.color);
    };

    /**
     * Set width and x-position for bars.
     */
    private setHorizontalPropsForBars = (): void => {
        this.bars.selectAll(`.${AVI_BAR_GRAPH_RECT_ACTUAL_CLASS}`)
            .attr('width', (datum: IAviBarGraphDataPoint): number => this.getBarWidth(datum))
            .attr('transform', (datum: IAviBarGraphDataPoint): string => {
                const xPosition = this.getXPositionFromTimestamp(datum.start);
                const translate = [xPosition, 0];

                return `translate(${translate})`;
            });
    };

    /**
     * Return width of single bar given data point.
     */
    private getBarWidth = (datum: IAviBarGraphDataPoint): number => {
        const graphContainerWidth = this.getGraphContainerWidth();

        if (graphContainerWidth <= pixelsBetweenBars) {
            return 0;
        }

        const {
            end,
            start,
        } = datum;

        const barWidth = this.getDurationInMilliSeconds(end, start);
        const graphDuration = this.getGraphDuration();

        const oneDivisionWidth = barWidth / graphDuration * this.getGraphContainerWidth();

        return oneDivisionWidth < pixelsBetweenBars ?
            oneDivisionWidth : oneDivisionWidth - pixelsBetweenBars;
    };

    /**
     * Return yScale needed for bars in graph.
     * Specifically for bars in graph, separate from scaling needed for y-axis.
     */
    private createBarYScale = (): d3.ScaleLinear<number, number> =>
        d3.scaleLinear()
            .domain([0, d3.max(
                this.datasetCopy,
                (datum: IAviBarGraphDataPoint) => datum.totalValueSummation,
            )])
            // Denote min to max range of any given datum's value
            .range([
                0,
                maxHeightOfBars,
            ]);

    /**
     * Create y-axis for bar graph.
     */
    private createYAxis = (): void => {
        const maxVal = d3.max(
            this.datasetCopy,
            (datum: IAviBarGraphDataPoint) => datum.totalValueSummation,
        );

        const yAxisScale = d3.scaleLinear()
            .domain([0, maxVal])
            // go backwards => bottom to top (negative)
            .range([actualGraphHeightPx - pixelsFromBottomOfParent, pixelsFromTopOfParent]);

        const yAxis = d3.axisLeft(yAxisScale)
            .tickSize(0)
            .tickFormat((num: number) => num2abbrStr(num, 0))
            .ticks(maxVal > 3 ? 4 : maxVal);

        this.svg
            .append(G_ELEMENT)
            .classed(AVI_BAR_GRAPH_Y_AXIS_CLASS, true)
            .call(yAxis)
            .attr('transform', `translate(${pixelsFromLeftOfParent}, 0)`);
    };

    /**
     * Create x-axis for bar graph.
     */
    private createXAxis = (): void => {
        const topTranslation = actualGraphHeightPx - pixelsFromBottomOfParent;

        this.svg
            .append(G_ELEMENT)
            .classed(AVI_BAR_GRAPH_X_AXIS_CLASS, true)
            .attr('transform', `translate(0, ${topTranslation})`)
            // @ts-expect-error
            .call(this.scaleXAxis());
    };

    /**
     * Return real-time width of graph container element.
     */
    private getGraphContainerWidth = (): number =>
        /* eslint-disable no-extra-parens */
        (document.querySelector(`.${AVI_BAR_GRAPH_VIEWPORT_CLASS}`) as HTMLElement)
            .offsetWidth;

    /**
     * Return starting horizontal position for provided timestamp.
     * Calculate ratio: (bar-start-time - graph-start-time)/(graph duration),
     * multiplies by graph width, and sets the bar at this position relative to svg container.
     */
    private getXPositionFromTimestamp = (timestamp: TTimestampFormatFromApi): number => {
        const milliSecondsFromStart =
            this.getDurationInMilliSeconds(timestamp, this.graphStartTimestampCopy);
        const xRatioFromStart = milliSecondsFromStart / this.getGraphDuration();

        return xRatioFromStart * (this.getGraphContainerWidth() - pixelsFromLeftOfParent);
    };

    /**
     * Return duration of any end+start timestamps in milliseconds.
     */
    private getDurationInMilliSeconds =
        (end: TTimestampFormatFromApi | Date, start: TTimestampFormatFromApi | Date): number => {
            return getUnixTimeInMilliseconds(end) - getUnixTimeInMilliseconds(start);
        };

    /**
     * Return duration of graph in milliseconds.
     */
    private getGraphDuration = (): number => {
        return this.getDurationInMilliSeconds(this.graphEndTimestampCopy, this.graphStartTimestampCopy);
    };

    /**
     * Return duration of graph in seconds.
     */
    private getGraphDurationInSeconds = (): number => {
        return this.getGraphDuration() / 1000;
    };

    /**
     * Calculate and update tooltip position.
     */
    private updateTooltipLeftPositionPx = (): void => {
        this.tooltipLeftPositionPx = this.getOverlayMiddlePixels() - 2 * pixelsFromLeftOfParent;
    };

    /**
     * Display UTC or local for user based on system setting.
     */
    private formatTimeForDisplay = (format: string): (d: Date) => string => {
        return this.useUtcTime ? d3.utcFormat(format) : d3.timeFormat(format);
    };

    /**
     * Scale x-axis.
     * Called upon change in parent container width (window size changes etc.).
     * Label are custom calculated to avoid issues inherent in d3's implementation.
     */
    private scaleXAxis = (): void => {
        const { graphStartTimestampCopy, graphEndTimestampCopy } = this;

        // Convert to usable date objects
        const startTime = new Date(graphStartTimestampCopy);
        const endTime = new Date(graphEndTimestampCopy);

        const timeSpan = endTime.getTime() - startTime.getTime();
        const totalDurationDays = timeSpan / (1000 * 60 * 60 * 24);

        // Limit the number of labels to prevent crowding/overlap, for worst case when page width
        // is 1200px, and left and right sidebars are both expanded.
        const maxAllowedTicks = 16;

        let tickValues = [];

        // Determined by visual trial and error to avoid cutting off the first and last labels
        const percentToAvoidCuttingOff = 0.02;
        // Adjust start and end times to avoid cutting off the first and last labels
        const adjustedStart = new Date(startTime.getTime() + timeSpan * percentToAvoidCuttingOff);
        const adjustedEnd = new Date(endTime.getTime() - timeSpan * percentToAvoidCuttingOff);
        const adjustedStartEpoch = adjustedStart.getTime();
        const adjustedEndEpoch = adjustedEnd.getTime();
        const adjustedDuration = adjustedEndEpoch - adjustedStartEpoch;

        if (totalDurationDays <= 1) {
            /*
             * For durations within a single day, we ensure that the labels are equally spaced
             * and accurate to the minute.
             */

            const tickInterval = adjustedDuration / (maxAllowedTicks - 1);

            tickValues = Array.from(
                { length: maxAllowedTicks },
                (_, i) => new Date(adjustedStartEpoch + i * tickInterval),
            );
        } else if (totalDurationDays > 1 && totalDurationDays <= 9) {
            /*
             * For durations between 1 and 9 days, we want to ensure that the labels are
             * equally spaced out between each day, that we display the start of each day,
             * and that labels between days are on the hour, for visual appeal.
             *
             * 9 is chosen as the upper limit for the following reasons:
             * 3 things are accomplished by the custom x-axis creation:
             * 1. avoid truncated text at left/right edges of axes.
             * 2. avoid overlap of labels caused by D3 edge case upon new month.
             * 3. for over 1 day duration, ensure that the labels are equally spaced out
             *  not only between the ends, but also between each day, and on the hour.
             * our maxAllowedTicks dictates the max number of days for which we can have labels
             * in between days. because of reason 1, 7 is too small a number, since 7 hours and
             * something will still allow for labels every midnight and noon. 10 is too large,
             * it may result in more than maxAllowedTicks.
             *
             * In the future, a dev may wish to compute the exact hours we want to set as max,
             * instead of 9 days, as a function of maxAllowedTicks, adjustedDuration, and keeping
             * 12 as the divisor. This will allow for a more dynamic approach, in case these
             * variables need to change. For now, 9 is a sufficient upper limit.
             */

            const totalHours = adjustedDuration / (1000 * 60 * 60);
            // Interval if we don't care to equally space between days
            const maxInterval = totalHours / maxAllowedTicks;

            /*
             * Calculate the actual interval to use, ensuring that the ticks/labels are
             * equally spaced out between 24 hours, and max number on entire axis does not
             * exceed maxAllowedTicks.
             *
             * To achieve this, the interval must be a divisor/factor of 24 (hours in a day),
             * but since we're limiting totalDurationDays to max of 9, we can use 12.
             * This results in allowance of 2, 3, 4, 6, 12 (hours) as possible intervals, without
             * having to explicitly compare to these divisors of 12.
             *
             * As an example, if maxInterval calculated above is 2.5,
             * we wish to use 3 as the interval.
             * Or if maxInterval is 4.5, we wish to use the next closest divisor of 12, which is 6.
             * The math below ensures this.
             */
            const actualInterval = Math.max(Math.ceil(12 / Math.floor(12 / maxInterval)), 2);

            /*
             * To find the first label value (dateToAdd[0]), we find the next hour after
             * adjustedStart that is a multiple of actualInterval.
             *
             * This is done by finding the remainder of adjustedStart's hours divided by
             * actualInterval, and then subtracting this from actualInterval to find the
             * time to the next multiple.
             * Finally we add this value to adjustedStart to get the first label value.
             */
            const remainderOverAdjustedStartTime = adjustedStart.getHours() % actualInterval;
            const timeToFirstTick = actualInterval - remainderOverAdjustedStartTime;
            const dateToAdd = new Date(adjustedStart.getTime());

            dateToAdd.setHours(adjustedStart.getHours() + timeToFirstTick);

            // Reset minutes to 0 to ensure the time label is exactly on the hour, instead of offset
            dateToAdd.setMinutes(0);

            while (dateToAdd < adjustedEnd) {
                tickValues.push(new Date(dateToAdd.getTime()));
                dateToAdd.setHours(dateToAdd.getHours() + actualInterval);
            }
        } else {
            /*
             * For durations over 9 days, focus on the start of each day only. Due to equal spacing
             * and desired maxAllowedTicks, we cannot show any labels in between start of days.
             */

            const daysBetweenTicks = Math.ceil(totalDurationDays / maxAllowedTicks);
            let current = d3.timeDay.ceil(adjustedStart);

            while (current <= adjustedEnd && tickValues.length < maxAllowedTicks) {
                tickValues.push(new Date(current));
                current = d3.timeDay.offset(current, daysBetweenTicks);
            }
        }

        /**
         * Annotate the labels with the appropriate format based on the time span.
         */
        const tickFormatFunction = (d: Date): string => {
            if (totalDurationDays <= 1) {
                return this.formatTimeForDisplay('%H:%M')(d);
            } else {
                const hour = new Date(d).getHours();

                if (hour === 0) {
                    // At the start of a new day, show day and date
                    return this.formatTimeForDisplay('%a %d')(d);
                } else {
                    // Otherwise, continue to show time for intraday ticks, if they exist
                    return this.formatTimeForDisplay('%I %p')(d);
                }
            }
        };

        // Scale the x-axis
        const xAxisScale = (this.useUtcTime ? d3.scaleUtc() : d3.scaleTime())
            // Note: '.domain' can alternatively be provided unix time in milliseconds.
            .domain([startTime, endTime])
            // offset to left to avoid last label spillage to right
            .range([pixelsFromLeftOfParent, this.getGraphContainerWidth() - 1]);

        // Assign labels
        // @ts-expect-error
        return d3.axisBottom(xAxisScale)
            .tickSize(0)
            .tickValues(tickValues)
            .tickFormat(tickFormatFunction);
    };

    /**
     * Update tooltip for logs that are being fetched into overlay.
     * Check for disappeared tooltip of a rendered overlay,
     * as this happens for incoming logs and create a new tooltip.
     */
    private updateTooltipForIncomingData(): void {
        const isTooltipAbsentForRenderedOverlay =
            !this.aviBarGraphTooltipRef.length && this.overlayExists();

        if (isTooltipAbsentForRenderedOverlay) {
            this.createCompletedOverlayTooltip(undefined);
        }
    }
}
